import React, { useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import { PackageSearch, Plus, Search } from "lucide-react";

const STATUSES = ["selected", "pending_review", "approved", "ready_for_publication"];

export default function AdminSellerSources() {
  const [sellerId, setSellerId] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [add, setAdd] = useState({ cj_pid: "", retail_price: "" });
  const [busy, setBusy] = useState(false);

  const load = async (id) => {
    const sid = id || sellerId;
    if (!sid.trim()) { toast.error("Enter a seller ID."); return; }
    try { const { data } = await adminClient().get(`/admin/cj/seller/${sid.trim()}/catalog`); setCatalog(data); }
    catch { toast.error("Could not load seller catalog."); }
  };

  const addProduct = async () => {
    if (!add.cj_pid.trim() || !add.retail_price) { toast.error("Product ID and retail price required."); return; }
    setBusy(true);
    try {
      const { data } = await adminClient().post(`/admin/cj/seller/${sellerId.trim()}/catalog/add`, {
        cj_pid: add.cj_pid.trim(), retail_price: Number(add.retail_price),
      });
      toast.success(data.duplicate_prevented ? "Already in catalog (duplicate prevented)." : `Added (min safe $${data.min_safe_price}).`);
      setAdd({ cj_pid: "", retail_price: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add product."); } finally { setBusy(false); }
  };

  const setStatus = async (cj_pid, status) => {
    try { await adminClient().post(`/admin/cj/seller/${sellerId.trim()}/catalog/${cj_pid}/status`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update status."); }
  };

  return (
    <div data-testid="admin-seller-sources">
      <div className="flex items-center gap-2 mb-1"><PackageSearch className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Seller Product Sources</h1></div>
      <p className="text-sm text-muted-foreground mb-5">Manage a seller's selected products from the shared master catalog. Manual selection only — <strong>never auto-published</strong>. One master product identity, no duplicates.</p>

      <div className="border border-black/10 bg-taven-gray/40 p-4 mb-6 flex flex-wrap items-end gap-3">
        <label className="text-sm flex-1 min-w-[220px]">Seller ID
          <input value={sellerId} onChange={(e) => setSellerId(e.target.value)} placeholder="e.g. seller-123" className="block w-full mt-1 border border-black/15 px-3 py-2" data-testid="seller-id-input" />
        </label>
        <button onClick={() => load()} className="flex items-center gap-1.5 bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="load-catalog-btn"><Search className="w-4 h-4" /> Load Catalog</button>
      </div>

      {catalog && (
        <>
          <div className="border border-black/10 bg-white p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="add-source-panel">
            <label className="text-sm flex-1 min-w-[180px]">Product ID (master)
              <input value={add.cj_pid} onChange={(e) => setAdd({ ...add, cj_pid: e.target.value })} className="block w-full mt-1 border border-black/15 px-3 py-2" data-testid="add-cj-pid" />
            </label>
            <label className="text-sm">Retail price
              <input type="number" value={add.retail_price} onChange={(e) => setAdd({ ...add, retail_price: e.target.value })} className="block mt-1 border border-black/15 px-3 py-2 w-28" data-testid="add-retail-price" />
            </label>
            <button onClick={addProduct} disabled={busy} className="flex items-center gap-1.5 bg-taven-gold text-black px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange hover:text-white disabled:opacity-50" data-testid="add-source-btn"><Plus className="w-4 h-4" /> Add to Store</button>
          </div>

          <p className="text-sm font-bold mb-2">{catalog.product_count} product(s) for {catalog.seller_id}</p>
          <div className="border border-black/10 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-taven-gray/50 text-left"><tr>
                <th className="p-3 font-bold">Product ID</th><th className="p-3 font-bold">SKU</th>
                <th className="p-3 font-bold">Retail</th><th className="p-3 font-bold">Min Safe</th>
                <th className="p-3 font-bold">Status</th>
              </tr></thead>
              <tbody>
                {catalog.items.map((it) => (
                  <tr key={it.id} className="border-t border-black/5" data-testid={`seller-item-${it.cj_pid}`}>
                    <td className="p-3 font-mono text-xs">{it.cj_pid}</td>
                    <td className="p-3 font-mono text-xs">{it.supplier_sku}</td>
                    <td className="p-3">${Number(it.retail_price).toFixed(2)}</td>
                    <td className="p-3 text-muted-foreground">${Number(it.min_safe_price).toFixed(2)}</td>
                    <td className="p-3">
                      <select value={it.status} onChange={(e) => setStatus(it.cj_pid, e.target.value)} data-testid={`status-${it.cj_pid}`} className="border border-black/15 px-2 py-1 text-xs">
                        {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {catalog.items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No products selected yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Setting a product to <strong>ready_for_publication</strong> flags it for a separate publish approval — it never writes to Shopify automatically.</p>
        </>
      )}
    </div>
  );
}
